/**
 * Ce que l'écran de contrôle lit en base.
 *
 * ## Pourquoi rien ne s'écrit ici
 *
 * Étape 1 d'un chemin en quatre : le grand livre MESURE, il ne corrige pas
 * encore. Ce module n'expose donc que des lectures. Les écarts qu'il rend sont
 * des décisions comptables à prendre — créditer, ou mettre en perte — et elles
 * se prennent dans les écrans qui savent déjà les écrire, pas ici.
 *
 * ## Le client passe par `payments/db`
 *
 * `src/lib/database.types.ts` est incomplet et ne connaît ni les trois vues de
 * ce chantier, ni celles du rapprochement. Le client re-typé sans schéma est
 * le compromis tenu en un seul endroit ; les formes de lignes sont donc
 * déclarées ici à la main, d'après la migration, qui fait référence.
 *
 * ## Le contrôle passe par une fonction, pas par la vue
 *
 * Mesuré le 20 septembre 2026, en rôle `authenticated` : la vue prenait
 * 6 507 ms et l'écran rendait des 500 (`57014`, statement timeout). Elle est
 * désormais figée dans une vue matérialisée, lue en 1 ms par
 * `controle_du_grand_livre()` — une fonction, parce qu'une vue matérialisée ne
 * porte pas de RLS et qu'il faut bien que quelque chose vérifie le rôle.
 */

import { db } from '../payments/db';
import { messageErreur } from '../payments/erreur';
import { lireLEcart, type FaitsDeLEcart, type LectureDeLEcart } from './ecart';

/** Un écart, avec les faits bruts ET leur lecture. */
export interface EcartDeFamille {
  faits: FaitsDeLEcart;
  lecture: LectureDeLEcart;
}

export interface Controle {
  ecarts: EcartDeFamille[];
  total: number;
  /** L'heure du calcul. `null` quand l'instantané n'a jamais été pris. */
  calculeLe: string | null;
}

/**
 * Les écarts, lus dans l'INSTANTANÉ.
 *
 * ## Pourquoi un instantané, et pourquoi une fonction
 *
 * La vue calculait 8 048 mouvements pour 1 505 familles à chaque ouverture, et
 * en rendait sept. En rôle `authenticated` — le seul qui compte, l'application
 * n'interroge jamais en `service_role` — cela prenait **6 507 ms**, au-delà du
 * `statement_timeout` de 8 s une fois le `count` de PostgREST ajouté. L'écran
 * rendait des 500, pas des lenteurs.
 *
 * L'instantané se lit en **1 ms**. Il se lit par une fonction et non par la
 * table, parce qu'une vue matérialisée ne porte pas de RLS : l'ouvrir à
 * `authenticated` donnerait à chaque parent le solde de toutes les familles.
 *
 * ## Il date, et il le dit
 *
 * `calculeLe` revient avec les lignes. Un chiffre de contrôle qui ne dit pas
 * son âge se lit comme frais, et une décision se prendrait sur un état
 * dépassé.
 */
export async function lireLesEcarts(): Promise<Controle> {
  const { data, error } = await db.rpc('controle_du_grand_livre');
  if (error) throw new Error(messageErreur(error));

  const lignes = (data ?? []) as unknown as Ligne[];
  const ecarts = lignes
    .map((l) => {
      const faits = versFaits(l);
      return { faits, lecture: lireLEcart(faits) };
    })
    /* Le plus lourd d'abord : c'est le dossier à ouvrir en premier. */
    .sort((a, b) => Math.abs(b.faits.ecartCents) - Math.abs(a.faits.ecartCents));

  return {
    ecarts,
    total: ecarts.length,
    calculeLe: lignes[0]?.calcule_le ?? null,
  };
}

/**
 * Recalcule l'instantané, et rend l'heure du nouveau calcul.
 *
 * C'est la seule écriture de ce module, et elle n'écrit aucune donnée métier :
 * elle refait un calcul. Le geste est explicite parce qu'il doit l'être — on
 * traite une liste d'anomalies, et une liste qui bouge pendant qu'on la traite
 * est pire qu'une liste qui date.
 */
export async function rafraichirLeControle(): Promise<string | null> {
  const { data, error } = await db.rpc('rafraichir_le_controle_du_grand_livre');
  if (error) throw new Error(messageErreur(error));
  return (data as unknown as string | null) ?? null;
}

/**
 * L'état du rapprochement, qui qualifie tout ce que l'écran affiche.
 *
 * Un solde n'est définitif que si tout ce qui a bougé en banque a été placé.
 * Au 19 septembre 2026 la file des entrées est vide, mais **589 virements
 * sortants** attendent encore — dont 45 que 39 familles peuvent revendiquer.
 * Afficher un solde sans ce chiffre serait présenter un provisoire comme un
 * définitif.
 */
export interface EtatDuRapprochement {
  entreesEnFile: number;
  sortiesEnFile: number;
  /** Celles qui citent une famille : ce sont elles qui rendent un solde provisoire. */
  sortiesRattachees: number;
  sortiesRattacheesCents: number;
  famillesProvisoires: number;
  /** Vrai tant qu'un mouvement bancaire n'est pas placé. */
  enCours: boolean;
}

export async function lireLEtatDuRapprochement(): Promise<EtatDuRapprochement> {
  /*
    Les deux files se COMPTENT en base, sans rapatrier leurs lignes. Seules les
    sorties rattachées à une famille sont lues en entier — elles sont 45 — pour
    en sommer le montant et compter les familles distinctes.

    Le total en euros de la file sortante n'est volontairement pas calculé :
    648 183,43 € au 19 septembre 2026, dont l'ONSS, Proximus et les écoles
    partenaires. Ce chiffre n'a rien à faire dans un écran de comptes de
    familles, et l'afficher là ferait croire à une dette envers elles.
  */
  const [entrees, sorties, rattachees] = await Promise.all([
    db
      .from('v_payment_allocation_state')
      .select('payment_id', { count: 'exact', head: true })
      .neq('state', 'allocated'),
    db
      .from('v_refund_allocation_state')
      .select('refund_id', { count: 'exact', head: true })
      .neq('state', 'allocated'),
    db
      .from('v_sorties_a_placer_par_famille')
      .select('user_id, unallocated_cents', { count: 'exact' })
      .limit(1000),
  ]);

  for (const r of [entrees, sorties, rattachees]) {
    if (r.error) throw new Error(messageErreur(r.error));
  }

  const lignesRattachees = (rattachees.data ?? []) as Array<{
    user_id: string;
    unallocated_cents: number;
  }>;

  const entreesEnFile = entrees.count ?? 0;
  const sortiesEnFile = sorties.count ?? 0;

  return {
    entreesEnFile,
    sortiesEnFile,
    sortiesRattachees: rattachees.count ?? 0,
    sortiesRattacheesCents: lignesRattachees.reduce(
      (t, l) => t + (l.unallocated_cents ?? 0),
      0,
    ),
    famillesProvisoires: new Set(lignesRattachees.map((l) => l.user_id)).size,
    enCours: entreesEnFile > 0 || sortiesEnFile > 0,
  };
}

/** Un mouvement du grand livre, tel que le détail d'une famille l'affiche. */
export interface MouvementDuLivre {
  ligneId: string;
  date: string | null;
  nature: 'facture' | 'note_de_credit' | 'encaissement' | 'remboursement';
  sens: 'debit' | 'credit';
  montantCents: number;
  signeCents: number;
  pieceNumero: string | null;
  pieceType: string | null;
  imputation: string | null;
}

export async function lireLeGrandLivre(userId: string): Promise<MouvementDuLivre[]> {
  const { data, error } = await db
    .from('v_grand_livre_famille')
    .select('ligne_id, date_piece, nature, sens, montant_cents, signe_cents, piece_numero, piece_type, imputation')
    .eq('user_id', userId)
    .order('date_piece', { ascending: true })
    .limit(500);
  if (error) throw new Error(messageErreur(error));

  return ((data ?? []) as unknown as LigneDuLivre[]).map((l) => ({
    ligneId: l.ligne_id,
    date: l.date_piece,
    nature: l.nature,
    sens: l.sens,
    montantCents: l.montant_cents ?? 0,
    signeCents: l.signe_cents ?? 0,
    pieceNumero: l.piece_numero,
    pieceType: l.piece_type,
    imputation: l.imputation,
  }));
}

/** Les virements sortants qu'une famille peut encore revendiquer. */
export interface SortieEnFile {
  refundId: string;
  date: string | null;
  montantCents: number;
  communication: string | null;
  rattachePar: string;
}

export async function lireLesSortiesDeLaFamille(userId: string): Promise<SortieEnFile[]> {
  const { data, error } = await db
    .from('v_sorties_a_placer_par_famille')
    .select('refund_id, value_date, unallocated_cents, communication_raw, rattache_par')
    .eq('user_id', userId)
    .order('value_date', { ascending: false })
    .limit(50);
  if (error) throw new Error(messageErreur(error));

  return ((data ?? []) as unknown as LigneDeSortie[]).map((l) => ({
    refundId: l.refund_id,
    date: l.value_date,
    montantCents: l.unallocated_cents ?? 0,
    communication: l.communication_raw,
    rattachePar: l.rattache_par,
  }));
}

/**
 * Les factures annulées qu'aucune note de crédit n'a entièrement éteintes.
 *
 * C'est le premier motif d'écart, et le seul qui appelle une décision que
 * l'administration a déjà prise : créditer. Les nommer ici évite de les
 * chercher à la main — et une décision qu'on prend en recopiant un numéro se
 * prend une fois sur deux.
 *
 * `outstanding_cents` est ce qui RESTE à créditer, pas le montant de la
 * facture : sur CDV-CSSA-2500539, 95,00 € facturés, 50,00 € déjà crédités, il
 * ne reste que 45,00 €. Émettre une note de 95,00 € créerait le motif d'à
 * côté — une facture créditée au-delà de son montant.
 */
export interface FactureACrediter {
  invoiceId: string;
  invoiceNumber: string;
  montantCents: number;
  dejaCrediteCents: number;
  resteACrediterCents: number;
  pdfUrl: string | null;
}

export async function lireLesFacturesACrediter(userId: string): Promise<FactureACrediter[]> {
  const { data, error } = await db
    .from('v_invoice_balances')
    .select('invoice_id, invoice_number, amount_cents, credit_notes_cents, outstanding_cents, pdf_url, status, user_id')
    .eq('user_id', userId)
    .eq('status', 'cancelled')
    .gt('outstanding_cents', 0)
    .order('invoice_number', { ascending: true })
    .limit(100);
  if (error) throw new Error(messageErreur(error));

  return ((data ?? []) as unknown as LigneDeFacture[]).map((l) => ({
    invoiceId: l.invoice_id,
    invoiceNumber: l.invoice_number,
    montantCents: l.amount_cents ?? 0,
    dejaCrediteCents: l.credit_notes_cents ?? 0,
    resteACrediterCents: l.outstanding_cents ?? 0,
    pdfUrl: l.pdf_url,
  }));
}

interface LigneDeFacture {
  invoice_id: string;
  invoice_number: string;
  amount_cents: number | null;
  credit_notes_cents: number | null;
  outstanding_cents: number | null;
  pdf_url: string | null;
}

// ---------------------------------------------------------------------------
//  Les formes que la base rend, et leur traduction
// ---------------------------------------------------------------------------

interface Ligne {
  user_id: string;
  prenom: string | null;
  nom: string | null;
  email: string | null;
  solde_cents: number | null;
  mouvements: number | null;
  solde_des_vues_cents: number | null;
  ecart_cents: number | null;
  annulees_non_creditees_cents: number | null;
  notes_excedentaires_cents: number | null;
  meme_euro_deux_fois_cents: number | null;
  notes_sans_facture_cents: number | null;
  avoir_cents: number | null;
  rendu_sur_notes_cents: number | null;
  sorties_en_file: number | null;
  sorties_en_file_cents: number | null;
  calcule_le: string | null;
}

interface LigneDuLivre {
  ligne_id: string;
  date_piece: string | null;
  nature: MouvementDuLivre['nature'];
  sens: MouvementDuLivre['sens'];
  montant_cents: number | null;
  signe_cents: number | null;
  piece_numero: string | null;
  piece_type: string | null;
  imputation: string | null;
}

interface LigneDeSortie {
  refund_id: string;
  value_date: string | null;
  unallocated_cents: number | null;
  communication_raw: string | null;
  rattache_par: string;
}

function versFaits(l: Ligne): FaitsDeLEcart {
  return {
    userId: l.user_id,
    prenom: l.prenom,
    nom: l.nom,
    email: l.email,
    soldeCents: l.solde_cents ?? 0,
    mouvements: l.mouvements ?? 0,
    soldeDesVuesCents: l.solde_des_vues_cents ?? 0,
    ecartCents: l.ecart_cents ?? 0,
    annuleesNonCrediteesCents: l.annulees_non_creditees_cents ?? 0,
    notesExcedentairesCents: l.notes_excedentaires_cents ?? 0,
    memeEuroDeuxFoisCents: l.meme_euro_deux_fois_cents ?? 0,
    notesSansFactureCents: l.notes_sans_facture_cents ?? 0,
    avoirCents: l.avoir_cents ?? 0,
    renduSurNotesCents: l.rendu_sur_notes_cents ?? 0,
    sortiesEnFile: l.sorties_en_file ?? 0,
    sortiesEnFileCents: l.sorties_en_file_cents ?? 0,
  };
}
